import { useEffect, useRef } from 'react';
import { useTerminalStore } from '@/stores/useTerminalStore';
import { websocketService } from '@/services/websocket';

interface TerminalPanelProps {
  sandboxId: string | null;
}

export default function TerminalPanel({ sandboxId }: TerminalPanelProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<any>(null);
  const fitAddonRef = useRef<any>(null);
  const sandboxIdRef = useRef(sandboxId);
  const { appendOutput } = useTerminalStore();

  // Keep the ref in sync with the prop so callbacks always see the latest value
  useEffect(() => {
    sandboxIdRef.current = sandboxId;
    // Print connection status change to terminal
    if (xtermRef.current) {
      xtermRef.current.writeln(
        sandboxId
          ? `\r\n\x1b[32mConnected to sandbox ${sandboxId.slice(0, 8)}\x1b[0m`
          : '\r\n\x1b[33mNo sandbox connected\x1b[0m'
      );
      xtermRef.current.write('$ ');
    }
  }, [sandboxId]);

  useEffect(() => {
    let mounted = true;

    async function initTerminal() {
      if (!terminalRef.current || xtermRef.current) return;

      try {
        const { Terminal } = await import('xterm');
        const { FitAddon } = await import('xterm-addon-fit');
        const { WebLinksAddon } = await import('xterm-addon-web-links');

        if (!mounted) return;

        const terminal = new Terminal({
          fontSize: 13,
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          theme: {
            background: '#1e1e1e',
            foreground: '#d4d4d4',
            cursor: '#d4d4d4',
          },
          cursorBlink: true,
          convertEol: true,
        });

        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        terminal.loadAddon(new WebLinksAddon());

        terminal.open(terminalRef.current!);
        fitAddon.fit();

        xtermRef.current = terminal;
        fitAddonRef.current = fitAddon;

        terminal.writeln('\x1b[33mZewst Terminal\x1b[0m');
        const currentId = sandboxIdRef.current;
        terminal.writeln(currentId ? `Connected to sandbox ${currentId.slice(0, 8)}` : 'No sandbox connected');
        terminal.write('\r\n$ ');

        // Handle user input — read sandboxId from ref to avoid stale closures
        let inputBuffer = '';
        terminal.onData((data) => {
          const sid = sandboxIdRef.current;
          if (!sid) return;

          if (data === '\r') {
            // Enter key
            terminal.write('\r\n');
            if (inputBuffer.trim()) {
              websocketService.emit('terminal:input', {
                sandboxId: sid,
                data: inputBuffer,
              });
            }
            inputBuffer = '';
          } else if (data === '\x7f') {
            // Backspace
            if (inputBuffer.length > 0) {
              inputBuffer = inputBuffer.slice(0, -1);
              terminal.write('\b \b');
            }
          } else {
            inputBuffer += data;
            terminal.write(data);
          }
        });
      } catch {
        // xterm not available
      }
    }

    initTerminal();

    return () => {
      mounted = false;
      xtermRef.current?.dispose();
      xtermRef.current = null;
    };
  }, []); // Terminal init runs once — sandboxId changes read from ref

  // Handle terminal output from WebSocket — filter by sandboxId
  useEffect(() => {
    const handleOutput = (data: { sandboxId: string; data: string }) => {
      if (data.sandboxId !== sandboxId) return;
      if (xtermRef.current) {
        xtermRef.current.write(data.data);
        xtermRef.current.write('\r\n$ ');
      }
      appendOutput(data.data);
    };

    websocketService.on('terminal:output', handleOutput);
    return () => {
      websocketService.off('terminal:output', handleOutput);
    };
  }, [appendOutput, sandboxId]);

  // Handle resize
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      fitAddonRef.current?.fit();
    });

    if (terminalRef.current) {
      observer.observe(terminalRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground border-b flex items-center justify-between">
        Terminal
        {sandboxId && (
          <span className="text-[10px] font-normal normal-case text-green-500">Connected</span>
        )}
      </div>
      <div ref={terminalRef} className="flex-1 p-1" />
    </div>
  );
}
